"use client";

import Link from "next/link";
import { useEffect } from "react";
import { useSelector } from "react-redux";


import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";

import Product from "@sharedComponents/cards/product";
import ShadowCard from "@sharedComponents/cards/shadow-card/";
import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import useSearchProductList from "@hooks/search/use-fetch-base-product-list";

import { getDesignedStoreIds } from "@selectors/store";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";

import { getMenuItemRoute } from "@constants/routes";
import { MY_DESIGNED_SWAG_LOCKER, WIDGETS_MENU_MAPPING } from "@constants/menu";

import styles from "./main.module.scss";

const emptyObj = {};

const MyDesignedSwagSection = ({ data, editingAllowed, children }) => {
  const designedStoreIds = useSelector(getDesignedStoreIds);
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const { intent, jsonContent } = data;
  const { title = "My Designed Swag", subtitle = "" } = JSON.parse(jsonContent) || emptyObj;
  const { count } = WIDGETS_MENU_MAPPING[intent];
  const { loading, fetchedProducts: products, getProductList } = useSearchProductList();
  const { storeBasePath, country, isGlobalCountry } = useStoreContext();

  useEffect(() => {
    if (designedStoreIds.length) {
      const variables = {
        designedStoreIds,
        ...getCommonGlobalCountryVariables({
          country,
          isGlobalCountry,
          countryKeyAttr: "countryCode",
        }),
        page: 1,
        perPage: count,
        withSwag: true,
        storeUpdatedAt: getStoreUpdatedAt(),
        ...getLockerShopESQueryVars({
          isLockerShop: true,
        }),
      };
      getProductList({ variables });
    }
  }, [designedStoreIds]);

  if (!designedStoreIds?.length || (!loading && products?.length === 0)) return null;

  const renderShimmerLoader = () => {
    return (
      <ShimmerWrapper
        length={count}
        cardClass={styles.colCard}
        wrapperClass={`${styles.productCardsWrapper} ${styles.xlFiveCard}`}
      >
        <ShadowCard />
      </ShimmerWrapper>
    );
  };

  const renderProducts = () => {
    return (
      <>
        <article className={`${styles.productCardsWrapper} ${styles.xlFiveCard}`}>
          {products.map((product) => (
            <Product
              data={product}
              className={styles.colCard}
              imageBlockClass={styles.imageBlock}
              productTileFooterClass={styles.tileFooter}
              key={product.id}
              isNextImage={true}
              unoptimized
            />
          ))}
        </article>
        <div className={styles.viewAllCta}>
          <Link
            href={getMenuItemRoute({
              storeBasePath,
              category: MY_DESIGNED_SWAG_LOCKER,
            })}
            prefetch={false}
          >
            View All
          </Link>
        </div>
      </>
    );
  };

  return (
    <section className={styles.containerClass}>
      <h3 className={styles.storeHeading}>{title}</h3>
      {subtitle ? <div className={styles.storeSubHeading}>{subtitle}</div> : null}
      {loading ? renderShimmerLoader() : renderProducts()}
      {editingAllowed && children}
    </section>
  );
};

export default MyDesignedSwagSection;
